import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import InputComponent from 'components/shared/InputComponent';
import { createStudySet } from 'services/Teacher/Vocabulary.service';
import Select from 'components/shared/Select'; // Assuming Select component is imported correctly
import { getAllSubject } from 'services/Teacher/Exercise.service';
import { useNavigate } from 'react-router-dom';
import { GetUserRoles } from 'utils/auth.utils';
import PageHeading from 'components/shared/PageHeading';
import { useTranslation } from 'react-i18next';
import useAutoSave from 'hooks/useAutoSave';
import AutoSaveIndicator from 'components/shared/AutoSaveIndicator';
import { getFormDraft, patchFormDraft, deleteFormDraft } from 'services/formDraft.service';

const ManualVocabulary = () => {
    const { t } = useTranslation();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const navigate = useNavigate();
    const userRole = GetUserRoles();
    const [rangeValue, setRangeValue] = useState(5);
    const [subjects, setSubjects] = useState([]);
    const [selectedSourceLanguage, setSelectedSourceLanguage] = useState("");
    const [selectedTargetLanguage, setSelectedTargetLanguage] = useState("");
    const [isSourceTargetSame, setIsSourceTargetSame] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true); // To handle form validation
    const [isTitleValid, setIsTitleValid] = useState(true); // For title validation

    // Draft auto-save for the manual vocabulary form — persists inputs
    // server-side after typing stops and restores them on return.
    const vocabDraftKey = "vocab-draft-manual";

    // Autosave stays OFF until the draft has been fetched and applied, so a
    // save can't fire during the mount race and overwrite the draft.
    const [hydrated, setHydrated] = useState(false);

    // Fetch subjects when the component mounts
    useEffect(() => {
        const fetchSubjects = async () => {
            try {
                const response = await getAllSubject();
                const formattedSubjects = response.data.results.subject.map(subject => ({
                    value: subject._id,
                    label: subject.title
                }));
                setSubjects(formattedSubjects);
                // Default the languages ONLY as a fallback — never override a
                // value the draft-restore effect may have already set (this
                // effect and restore race on mount; without the guard, the
                // default clobbers a restored selection on refresh).
                setSelectedSourceLanguage((prev) => prev || formattedSubjects[0]?.value || "");
                setSelectedTargetLanguage((prev) => prev || formattedSubjects[1]?.value || "");
            } catch (error) {
                console.error("Error fetching subjects:", error);
            }
        };

        fetchSubjects();
    }, []);

    // Restore any saved draft on mount, then enable autosave.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getFormDraft(vocabDraftKey);
                const d = res?.data?.result;
                if (!cancelled && d && typeof d === "object" && Object.keys(d).length) {
                    if (typeof d.title === "string") setTitle(d.title);
                    if (typeof d.description === "string") setDescription(d.description);
                    if (typeof d.selectedSourceLanguage === "string")
                        setSelectedSourceLanguage(d.selectedSourceLanguage);
                    if (typeof d.selectedTargetLanguage === "string")
                        setSelectedTargetLanguage(d.selectedTargetLanguage);
                    if (d.rangeValue != null) setRangeValue(d.rangeValue);
                }
            } catch (e) {
                /* ignore */
            } finally {
                if (!cancelled) setHydrated(true);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        status: vocabAutoSaveStatus,
        lastSavedAt: vocabAutoSaveAt,
        isOnline: vocabAutoSaveOnline,
    } = useAutoSave(
        {
            title,
            description,
            selectedSourceLanguage,
            selectedTargetLanguage,
            rangeValue,
        },
        (changed) => patchFormDraft(vocabDraftKey, changed),
        { enabled: hydrated }
    );

    // Handle the form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validate title, source, and target language
        if (!title.trim()) {
            setIsTitleValid(false);
            setIsFormValid(false);
            return;
        }

        // Validate that both source and target languages are selected and not the same
        if (!selectedSourceLanguage || !selectedTargetLanguage) {
            setIsFormValid(false);
            return;
        }

        if (selectedSourceLanguage === selectedTargetLanguage) {
            setIsSourceTargetSame(true);
            setIsFormValid(false);
            return;
        }

        const payload = {
            title,
            description,
            maxSize: rangeValue,
            sourceLanguage: selectedSourceLanguage,
            targetLanguage: selectedTargetLanguage,
            difficulty: 'Beginner', // Example difficulty, change as needed
            isLibraryStudySet: userRole === "superUser" ? true : false

        };

        const toastId = toast.loading(t('Adding Study set'));
        try {
            const response = await createStudySet(payload);
            await deleteFormDraft(vocabDraftKey).catch(() => {});
            navigate(`/vocabulary/${response.data?.result?._id}`);
            toast.update(toastId, {
                render: t('Study set successfully created.'),
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
        } catch (error) {
            console.log("Error:", error);
            toast.update(toastId, {
                render: t(`Error creating Study set`),
                type: 'error',
                isLoading: false,
                autoClose: 5000,
            });
        }
    };

    return (
        <>
            <PageHeading heading={"Create Vocabulary"} />
            <Card className='border-0 shadow p-3 mt-4'>
                <div className="d-flex justify-content-end mb-2">
                    <AutoSaveIndicator draft
                        status={vocabAutoSaveStatus}
                        lastSavedAt={vocabAutoSaveAt}
                        isOnline={vocabAutoSaveOnline}
                    />
                </div>
                <Form>
                    <Row>
                        <Col xs={12} lg={6}>
                            <InputComponent
                                label={t("Title")}
                                placeholder={t("Study set title")}
                                value={title}
                                setValue={setTitle}
                                type={"text"}
                                required
                                isInvalid={!isTitleValid} // Add validation error for title
                            />
                            {!isTitleValid && (
                                <p style={{ color: 'red' }}>{t("Title is required")}.</p>
                            )}
                        </Col>
                        <Col xs={12} lg={6}>
                            <InputComponent
                                label={t("Description")}
                                placeholder={t("A short description")}
                                value={description}
                                setValue={setDescription}
                                type={"text"}
                                optional
                            />
                        </Col>

                        {/* Subject (formerly Target Language) */}
                        <Col xs={12} lg={6}>
                            <Select
                                heading={t("Subject")}
                                value={selectedTargetLanguage}
                                onChange={setSelectedTargetLanguage}
                                options={subjects}
                                ariaLabel={t("Select subject")}
                                required
                                isInvalid={!isFormValid || isSourceTargetSame}
                            />
                        </Col>

                        {/* Source Language */}
                        <Col xs={12} lg={6}>
                            <Select
                                heading={t("Source Language")}
                                value={selectedSourceLanguage}
                                onChange={setSelectedSourceLanguage}
                                options={subjects}
                                ariaLabel={t("Select source language")}
                                required
                                isInvalid={!isFormValid}
                            />
                        </Col>

                        {/* Size Range */}
                        <Col xs={12} lg={6}>
                            <Form.Group controlId="formRange">
                                <Form.Label>{t("Size")}: {rangeValue}</Form.Label>
                                <Form.Range
                                    min={5}
                                    max={20}
                                    value={rangeValue}
                                    onChange={(e) => setRangeValue(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        {isSourceTargetSame && (
                            <p style={{ color: 'red' }}>{t("Source and Target language cannot be the same.")}</p>
                        )}
                        {!isFormValid && (selectedSourceLanguage === "" || selectedTargetLanguage === "") && (
                            <p style={{ color: 'red' }}>{t("Both Source and Target languages are required.")}</p>
                        )}
                    </Row>
                </Form>

                <div className="d-flex justify-content-end mt-3">
                    <div>
                        <Button className='button-style border-0 py-2 px-4' onClick={handleSubmit}>
                            {t("Create")}
                        </Button>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default ManualVocabulary;
